import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    Box,
    Button,
    Backdrop,
    CircularProgress,
    Typography,
    Chip,
    TextField,
    InputAdornment,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
} from "@mui/material";
import {
    Info,
    PlayArrow,
    Campaign as CampaignIcon,
    Search,
    FormatListBulleted,
    Schedule,
    Edit,
    StopCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../api/axiosInstance";
import useNotification from "../../common/Notification";
import { blue, green, orange, red, grey } from "@mui/material/colors";
import CampaignStatusModal from "./CampaignStatusModal";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";

const CampaignManagement = () => {
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pagination, setPagination] = useState({});
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    // Preserve retry state locally to prevent it from changing when campaign is stopped/started
    const [preservedRetryState, setPreservedRetryState] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const showNotification = useNotification();
    const isLoadingRef = useRef(false);
    const currentPageRef = useRef(1);
    const totalPagesRef = useRef(1);
    const hasInitialLoadRef = useRef(false);

    const fetchCampaigns = useCallback(async (page = 1, append = false) => {
        if (isLoadingRef.current) {
            return;
        }

        isLoadingRef.current = true;
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        try {
            const response = await axiosInstance.get(
                `/whatsapp-business/campaign/list?page=${page}&per_page=20`
            );
            if (response.data.success) {
                const newCampaigns = response.data.data || [];
                if (append) {
                    setCampaigns((prev) => [...prev, ...newCampaigns]);
                } else {
                    setCampaigns(newCampaigns);
                }
                setPagination(response.data.pagination || {});
                const lastPage = response.data.pagination?.last_page || 1;
                setTotalPages(lastPage);
                setCurrentPage(page);
                currentPageRef.current = page;
                totalPagesRef.current = lastPage;
            }
        } catch (error) {
            console.error("Error fetching campaigns:", error);
            showNotification(
                error.response?.data?.message || "Failed to fetch campaigns",
                "error"
            );
        } finally {
            setLoading(false);
            setLoadingMore(false);
            isLoadingRef.current = false;
        }
    }, [showNotification]);



    useEffect(() => {
        if (!hasInitialLoadRef.current) {
            hasInitialLoadRef.current = true;
            fetchCampaigns(1, false);
        }
    }, []);

    const handleScroll = useCallback(() => {
        if (isLoadingRef.current || currentPageRef.current >= totalPagesRef.current) {
            return;
        }

        const scrollPosition = window.innerHeight + window.scrollY;
        const documentHeight = document.documentElement.scrollHeight;
        const threshold = 500;

        if (scrollPosition >= documentHeight - threshold) {
            const nextPage = currentPageRef.current + 1;
            if (nextPage <= totalPagesRef.current) {
                fetchCampaigns(nextPage, true);
            }
        }
    }, [fetchCampaigns]);

    useEffect(() => {
        let timeoutId;
        const throttledHandleScroll = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(() => {
                handleScroll();
            }, 200);
        };

        window.addEventListener("scroll", throttledHandleScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", throttledHandleScroll);
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [handleScroll]);

    const handleViewStatus = async (campaignName) => {
        setSelectedCampaign(campaignName);
        setStatusModalOpen(true);
    };


    const handleToggleRetry = async (campaign, enable) => {
        setLoading(true);
        try {
            const response = await axiosInstance.put(
                `/whatsapp-business/campaign/retry`,
                {
                    campaign_name: campaign.campaign_name,
                    template_name: campaign.template_name,
                    template_language: campaign.template_language,
                    enable: enable,
                }
            );
            if (response.data.success) {
                // Update preserved retry state
                const campaignKey = `${campaign.campaign_name}_${campaign.template_name}_${campaign.template_language}`;
                setPreservedRetryState((prev) => ({
                    ...prev,
                    [campaignKey]: enable,
                }));
                showNotification(response.data.message, "success");
                // Refresh campaigns to get updated auto_retrying status
                fetchCampaigns(currentPage, false);
            }
        } catch (error) {
            console.error("Error toggling retry:", error);
            showNotification(
                error.response?.data?.message ||
                    "Failed to update retry settings",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleStartStop = async (campaign, action) => {
        console.log("handleStartStop called with:", { campaign: campaign.campaign_name, action });
        setLoading(true);
        try {
            // Preserve the current retry state before stopping/starting
            const campaignKey = `${campaign.campaign_name}_${campaign.template_name}_${campaign.template_language}`;
            if (campaign.auto_retrying !== undefined) {
                setPreservedRetryState((prev) => ({
                    ...prev,
                    [campaignKey]: campaign.auto_retrying,
                }));
            }

            const response = await axiosInstance.put(
                `/whatsapp-business/campaign/status`,
                {
                    campaign_name: campaign.campaign_name,
                    template_name: campaign.template_name,
                    template_language: campaign.template_language,
                    action: action,
                }
            );
            console.log("handleStartStop API response:", response.data);
            if (response.data.success) {
                showNotification(response.data.message, "success");
                // Refresh campaigns to get updated status
                fetchCampaigns(currentPage, false);
            }
        } catch (error) {
            console.error("Error updating campaign status:", error);
            showNotification(
                error.response?.data?.message ||
                    "Failed to update campaign status",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleViewMessageQueue = (campaignName) => {
        navigate(`/whatsapp-business/message-queue?campaign_name=${encodeURIComponent(campaignName)}`);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            running: {
                label: "Running",
                icon: <PlayArrow sx={{ fontSize: 14 }} />,
                bgColor: green[100],
                textColor: green[700],
                darkBgColor: green[900],
                darkTextColor: green[400],
            },
            stopped: {
                label: "Stopped",
                icon: <StopCircle sx={{ fontSize: 14 }} />,
                bgColor: red[100],
                textColor: red[700],
                darkBgColor: red[900],
                darkTextColor: red[400],
            },
            pending: {
                label: "Pending",
                icon: <Schedule sx={{ fontSize: 14 }} />,
                bgColor: orange[100],
                textColor: orange[700],
                darkBgColor: orange[900],
                darkTextColor: orange[400],
            },
            complete: {
                label: "Completed",
                icon: <CampaignIcon sx={{ fontSize: 14 }} />,
                bgColor: blue[100],
                textColor: blue[700],
                darkBgColor: blue[900],
                darkTextColor: blue[400],
            },
        };

        const config = statusConfig[status] || {
            label: "Draft",
            icon: <Edit sx={{ fontSize: 14 }} />,
            bgColor: grey[100],
            textColor: grey[600],
            darkBgColor: grey[800],
            darkTextColor: grey[400],
        };

        return (
            <Chip
                icon={config.icon}
                label={config.label}
                size="small"
                sx={{
                    backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                            ? config.darkBgColor
                            : config.bgColor,
                    color: (theme) =>
                        theme.palette.mode === "dark"
                            ? config.darkTextColor
                            : config.textColor,
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    height: 24,
                    borderRadius: 1.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    "& .MuiChip-icon": {
                        color: (theme) =>
                            theme.palette.mode === "dark"
                                ? config.darkTextColor
                                : config.textColor,
                    },
                }}
            />
        );
    };

    const filteredCampaigns = campaigns.filter((campaign) =>
        campaign.campaign_name
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
    );

    return (
        <Box
            sx={{
                ...WB_SX.page,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                overflowX: "hidden",
            }}
        >
            <Box sx={{ ...WB_SX.headerRow, alignItems: { xs: "flex-start", md: "center" }, mb: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            backgroundColor: WB_UI.color.softInfoBg,
                            color: WB_UI.color.primaryButtonBg,
                            border: `1px solid ${WB_UI.color.border}`,
                        }}
                    >
                        <CampaignIcon />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h5" sx={{ ...WB_SX.pageTitle, mb: 0.25 }}>
                            Campaign Management
                        </Typography>
                        <Typography variant="body2" sx={WB_SX.pageSubtitle}>
                            Manage WhatsApp campaigns, retries, and message queue controls.
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                        size="small"
                        label={`${pagination?.total ?? campaigns.length ?? 0} total`}
                        sx={{
                            fontWeight: 700,
                            borderRadius: 2,
                            backgroundColor: WB_UI.color.softInfoBg,
                            border: `1px solid ${WB_UI.color.border}`,
                        }}
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => fetchCampaigns(1, false)}
                        disabled={loading}
                        sx={WB_SX.secondaryButton}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ ...WB_SX.card, mb: 2 }}>
                <Box
                    sx={{
                        p: 2,
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 1.5,
                        alignItems: { xs: "stretch", sm: "center" },
                    }}
                >
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search campaign name…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                backgroundColor: "#fff",
                            },
                        }}
                    />
                    <Chip
                        size="small"
                        label={`Showing ${filteredCampaigns.length}`}
                        sx={{
                            fontWeight: 700,
                            borderRadius: 2,
                            alignSelf: { xs: "flex-start", sm: "center" },
                            backgroundColor: WB_UI.color.softInfoBg,
                            border: `1px solid ${WB_UI.color.border}`,
                        }}
                    />
                </Box>
            </Paper>

            <Alert
                severity="info"
                icon={<Info fontSize="small" />}
                sx={{
                    mb: 2,
                    borderRadius: 2,
                    border: `1px solid ${WB_UI.color.border}`,
                    backgroundColor: WB_UI.color.softInfoBg,
                    color: "text.primary",
                    "& .MuiAlert-icon": { color: WB_UI.color.primaryButtonBg },
                    "& .MuiAlert-message": { fontWeight: 600 },
                }}
            >
                Completed campaigns do not allow modification of Start/Stop or Auto Retry (On/Off) settings.
            </Alert>

            <Backdrop
                sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 1,
                })}
                open={loading && !loadingMore}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    ...WB_SX.tableContainer,
                    mb: 2,
                    maxWidth: "100%",
                }}
            >
                <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
                    <TableHead>
                        <TableRow>
                            {[
                                { label: "Campaign", align: "left" },
                                { label: "Messages", align: "right" },
                                { label: "Status", align: "left" },
                                { label: "Start / Stop", align: "left" },
                                { label: "Auto Retry", align: "left" },
                                { label: "Actions", align: "center" },
                            ].map((h) => (
                                <TableCell
                                    key={h.label}
                                    align={h.align}
                                    sx={{
                                        ...WB_SX.tableHeadCell,
                                        py: 1.5,
                                    }}
                                >
                                    {h.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredCampaigns.length === 0 && !loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <CampaignIcon sx={{ fontSize: 36, color: "text.disabled" }} />
                                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                            {searchQuery
                                                ? "No campaigns found matching your search"
                                                : "No campaigns found"}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : filteredCampaigns.map((campaign, index) => {
                            const status = campaign.status || "pending";
                            const campaignKey = `${campaign.campaign_name}_${campaign.template_name}_${campaign.template_language}`;
                            const retryEnabled = preservedRetryState[campaignKey] !== undefined
                                ? preservedRetryState[campaignKey]
                                : campaign.auto_retrying === true;

                            return (
                                <TableRow
                                    key={`${campaignKey}_${index}`}
                                    hover
                                    sx={{
                                        ...WB_SX.tableRow,
                                        "&:hover": { bgcolor: WB_UI.color.softInfoBg },
                                        "& td": {
                                            borderBottom: `1px solid ${WB_UI.color.border}`,
                                            verticalAlign: "middle",
                                        },
                                    }}
                                >
                                    <TableCell sx={{ maxWidth: 280 }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={700}
                                            sx={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                            title={campaign.campaign_name}
                                        >
                                            {campaign.campaign_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {campaign.template_name || "—"} · {campaign.template_language || "—"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight={700}>
                                            {campaign.total_messages_scheduled?.toLocaleString() ?? 0}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{getStatusBadge(status)}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Switch
                                                size="small"
                                                checked={status === "running"}
                                                onChange={() => {
                                                    const action = status === "running" ? "stop" : "start";
                                                    handleStartStop(campaign, action);
                                                }}
                                                disabled={status === "complete"}
                                                sx={{
                                                    "& .MuiSwitch-switchBase.Mui-checked": { color: green[600] },
                                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                        backgroundColor: green[600],
                                                    },
                                                }}
                                            />
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                {status === "running"
                                                    ? "Running"
                                                    : status === "stopped"
                                                    ? "Stopped"
                                                    : status === "pending"
                                                    ? "Pending"
                                                    : "Complete"}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Switch
                                                size="small"
                                                checked={retryEnabled}
                                                onChange={() => handleToggleRetry(campaign, !retryEnabled)}
                                                disabled={status === "complete"}
                                                sx={{
                                                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#111827" },
                                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                        backgroundColor: "#111827",
                                                    },
                                                }}
                                            />
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                {retryEnabled ? "On" : "Off"}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.75,
                                                justifyContent: "center",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<Info sx={{ fontSize: 16 }} />}
                                                onClick={() => handleViewStatus(campaign.campaign_name)}
                                                sx={{
                                                    ...WB_SX.secondaryButton,
                                                    minWidth: "auto",
                                                    px: 1.25,
                                                    py: 0.5,
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                Status
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<FormatListBulleted sx={{ fontSize: 16 }} />}
                                                onClick={() => handleViewMessageQueue(campaign.campaign_name)}
                                                sx={{
                                                    ...WB_SX.primaryButton,
                                                    minWidth: "auto",
                                                    px: 1.25,
                                                    py: 0.5,
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                Queue
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {loadingMore && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={28} sx={{ color: WB_UI.color.primaryButtonBg }} />
                </Box>
            )}

            {pagination.total > 0 && (
                <Paper elevation={0} sx={{ ...WB_SX.card, px: 2, py: 1.5 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
                        Showing {filteredCampaigns.length} of {pagination.total} campaigns
                        {currentPage > 1 ? ` · Page ${currentPage}${totalPages ? ` / ${totalPages}` : ""}` : ""}
                    </Typography>
                </Paper>
            )}

            <CampaignStatusModal
                open={statusModalOpen}
                onClose={() => {
                    setStatusModalOpen(false);
                    setSelectedCampaign(null);
                }}
                campaignName={selectedCampaign}
            />
        </Box>
    );
};

export default CampaignManagement;
